'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from './useIsMobile';
import { useIsMobileOverride } from '@jlg-website/editor';
import type { TopDistance, Direction } from '@/components/pages/navbar/type';

const scrollYPositionTrigger = 130;
const navbarHiddenOffset = -200;
const bottomNavbarHiddenOffset = -100;
const LG_BREAKPOINT = 1024;
const ELEMENTS_TO_HIDE_NAVBOTTOM = ['form', 'footer'];

const DATA_MENU_SELECTOR = '[data-menu]';

function topFromContainer(isMobile: boolean, serverInitialTop: TopDistance): number {
  if (typeof window === 'undefined')
    return isMobile ? serverInitialTop.mobile : serverInitialTop.desktop;
  return window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
}

function someElementVisible(elements: Element[]): boolean {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return elements.some((el) => {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < viewportHeight;
  });
}

export function useNavbar(serverInitialTop: TopDistance, minTop: TopDistance) {
  const isMobile = useIsMobile();
  const mobileOverride = useIsMobileOverride();
  const [initialTop, setInitialTop] = useState(() => topFromContainer(isMobile, serverInitialTop));
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [top, setTop] = useState<number | null>(null);
  const [transitionToTop, setTransitionToTop] = useState('none');
  const [bottom, setBottom] = useState(bottomNavbarHiddenOffset);

  const ticking = useRef(false);
  const lastScrollY = useRef(0);
  const currentTop = useRef(initialTop);

  const [prevMobileOverride, setPrevMobileOverride] = useState(mobileOverride);
  if (mobileOverride !== prevMobileOverride) {
    setPrevMobileOverride(mobileOverride);
    if (mobileOverride !== null) {
      const newTop = mobileOverride ? serverInitialTop.mobile : serverInitialTop.desktop;
      setInitialTop(newTop);
      setTop(newTop);
    }
  }
  const scrollDirection = useRef<Direction>('down');
  const elementsToHideNavBottomRef = useRef<Element[]>([]);

  const toggleMenuOpen = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY.current;
      lastScrollY.current = scrollY;

      const direction = delta >= 0 ? 'down' : 'up';

      if (direction !== scrollDirection.current) {
        scrollDirection.current = direction;
        setTransitionToTop(direction === 'up' ? 'top 0.3s ease-out' : 'none');
      }

      if (isMobile) {
        setBottom((prev) => {
          const next =
            direction === 'down' && !someElementVisible(elementsToHideNavBottomRef.current)
              ? 0
              : bottomNavbarHiddenOffset;
          return prev !== next ? next : prev;
        });
      }

      const minVal = isMobile ? minTop.mobile : minTop.desktop;
      const newTop =
        scrollY <= initialTop
          ? initialTop
          : Math.min(minVal, Math.max(navbarHiddenOffset, currentTop.current - delta));
      if (newTop !== currentTop.current) {
        currentTop.current = newTop;
        setTop(newTop);
      }
      if (newTop < 0) {
        setIsMenuOpen(false);
      }

      const scrolledState = scrollY > scrollYPositionTrigger;
      setIsScrolled((prev) => (prev !== scrolledState ? scrolledState : prev));

      ticking.current = false;
    });
  }, [initialTop, minTop, isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    const selector = ELEMENTS_TO_HIDE_NAVBOTTOM.join(',');
    const visibleElements = new Set<Element>();
    const observedElements = new Set<Element>();

    elementsToHideNavBottomRef.current = [];

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleElements.add(entry.target);
          else visibleElements.delete(entry.target);
        });
        if (visibleElements.size > 0) setBottom(bottomNavbarHiddenOffset);
      },
      { threshold: 0 }
    );

    const observe = (el: Element) => {
      if (observedElements.has(el)) return;
      observedElements.add(el);
      elementsToHideNavBottomRef.current.push(el);
      const rect = el.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? window.innerHeight;
      if (rect.bottom > 0 && rect.top < vh) visibleElements.add(el);
      intersectionObserver.observe(el);
    };

    document.querySelectorAll(selector).forEach(observe);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          const el = node as Element;
          if (el.matches(selector)) observe(el);
          el.querySelectorAll(selector).forEach(observe);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMobile]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    currentTop.current = initialTop;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, initialTop]);

  useEffect(() => {
    const handleResize = () => {
      const newInitialTop =
        window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
      setInitialTop(newInitialTop);
      currentTop.current = newInitialTop;
      setTop(newInitialTop);
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [serverInitialTop]);

  useEffect(() => {
    if (isMobile) document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      if (isMobile) document.body.style.overflow = '';
    };
  }, [isMenuOpen, isMobile]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target?.closest?.(DATA_MENU_SELECTOR)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const scrollToForm = () => {
    const element = document.querySelector(
      'footer input[name="lead.contact.first_name"]'
    ) as HTMLInputElement | null;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      element.focus();
    }
  };

  return {
    isMobile,
    isScrolled,
    isMenuOpen,
    toggleMenuOpen,
    top: top || undefined,
    transitionToTop,
    bottom,
    scrollToForm,
  };
}
